import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import cashboxService from '../services/cashboxService';
import { toast } from 'react-hot-toast';
import {
    Wallet,
    Plus,
    TrendingUp,
    TrendingDown,
    History,
    Search,
    Filter,
    Trash2,
    X,
    Calendar,
    DollarSign,
    ArrowUpRight,
    ArrowDownRight,
    PieChart,
    Download,
    FileText,
    ArrowDownCircle,
    AlertCircle,
    Upload,
    RefreshCw,
    QrCode,
    Banknote,
    Building2,
    Scale,
    CreditCard,
    Layers,
    RotateCcw,
    CheckCircle,
    User,
    Paperclip
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CashboxInjection from './CashboxInjection';
import ConfirmModal from './ConfirmModal';

const CompanyCashbox = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showInjection, setShowInjection] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterPaymentMethod, setFilterPaymentMethod] = useState('');

    const [filterMonth, setFilterMonth] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const [formData, setFormData] = useState({
        transaction_type: 'INCOME',
        category_code: '',
        description: '',
        amount: '',
        reference_date: format(new Date(), 'yyyy-MM-dd'),
        payment_method: 'TRANSFERENCIA',
        bank_account: '',
        document_number: '',
        notes: '',
        parent_id: null
    });

    const [expandedTransaction, setExpandedTransaction] = useState(null);
    const [transactionDetails, setTransactionDetails] = useState(null);
    const [isDetailsLoading, setIsDetailsLoading] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, transactionId: null });
    const [showRecalculateConfirm, setShowRecalculateConfirm] = useState(false);

    const queryClient = useQueryClient();

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 2000);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Validação de string de data (só envia para o backend se tiver 4 dígitos válidos no ano)
    const isValidDateStr = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string') return false;
        const parts = dateStr.trim().split('-');
        if (parts.length !== 3) return false;
        const [y, m, d] = parts.map(Number);
        if (!y || !m || !d) return false;
        if (y < 2000 || y > 2099) return false;
        if (m < 1 || m > 12) return false;
        if (d < 1 || d > 31) return false;
        return true;
    };

    const validStartDate = useMemo(() => {
        return isValidDateStr(filterStartDate) ? filterStartDate.trim() : '';
    }, [filterStartDate]);

    const validEndDate = useMemo(() => {
        return isValidDateStr(filterEndDate) ? filterEndDate.trim() : '';
    }, [filterEndDate]);

    const isDateRangeInvalid = useMemo(() => {
        if (validStartDate && validEndDate) {
            return validStartDate > validEndDate;
        }
        return false;
    }, [validStartDate, validEndDate]);

    // Queries
    const { data: statsData, isLoading: isLoadingStats } = useQuery({
        queryKey: ['cashbox-stats'],
        queryFn: () => cashboxService.getStats()
    });

    const { data: categoriesData } = useQuery({
        queryKey: ['cashbox-categories'],
        queryFn: () => cashboxService.getCategories()
    });

    const { data: transactionsData, isLoading: isLoadingTransactions } = useQuery({
        queryKey: ['cashbox-transactions', { 
            search: debouncedSearchTerm, 
            type: filterType, 
            category: filterCategory, 
            paymentMethod: filterPaymentMethod, 
            month: filterMonth, 
            year: filterYear, 
            startDate: !isDateRangeInvalid ? validStartDate : '', 
            endDate: !isDateRangeInvalid ? validEndDate : '', 
            page: currentPage, 
            limit: pageSize 
        }],
        queryFn: () => cashboxService.getTransactions({
            search: debouncedSearchTerm || undefined,
            type: filterType || undefined,
            category: filterCategory || undefined,
            payment_method: filterPaymentMethod || undefined,
            month: filterMonth || undefined,
            year: filterYear || undefined,
            startDate: (!isDateRangeInvalid && validStartDate) ? validStartDate : undefined,
            endDate: (!isDateRangeInvalid && validEndDate) ? validEndDate : undefined,
            page: currentPage,
            limit: pageSize
        })
    });

    // Query para detalhes de transação - CACHEADO
    const { data: transactionDetailsData, refetch: refetchTransactionDetails } = useQuery({
        queryKey: ['cashbox-transaction-details', expandedTransaction],
        queryFn: () => expandedTransaction ? cashboxService.getTransactionDetails(expandedTransaction) : null,
        enabled: false // Só executa quando chamado manualmente
    });

    // Mutations
    const createMutation = useMutation({
        mutationFn: cashboxService.createTransaction,
        onSuccess: () => {
            toast.success('Transação registrada com sucesso!');
            queryClient.invalidateQueries(['cashbox-transactions']);
            queryClient.invalidateQueries(['cashbox-stats']);
            closeModal();
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Erro ao registrar transação');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: cashboxService.deleteTransaction,
        onSuccess: () => {
            toast.success('Transação excluída e saldo atualizado!');
            queryClient.invalidateQueries(['cashbox-transactions']);
            queryClient.invalidateQueries(['cashbox-stats']);
            // Limpar cache de detalhes e estado se estava expandido
            if (expandedTransaction) {
                queryClient.removeQueries(['cashbox-transaction-details', expandedTransaction]);
                setTransactionDetails(null);
                setExpandedTransaction(null);
            }
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Erro ao excluir transação');
        }
    });

    const recalculateMutation = useMutation({
        mutationFn: cashboxService.recalculateBalance,
        onSuccess: (data) => {
            const totalProcessed = data?.data?.totalProcessed || 0;
            toast.success(`Caixa auditado com sucesso! ${totalProcessed} movimentação(ões) reconciliada(s).`);
            queryClient.invalidateQueries(['cashbox-stats']);
            queryClient.invalidateQueries(['cashbox-transactions']);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Erro ao recalcular saldo');
        }
    });

    const syncPayslipsMutation = useMutation({
        mutationFn: cashboxService.syncPayslips,
        onSuccess: (data) => {
            const count = data?.syncedCount !== undefined ? data.syncedCount : (data?.data?.syncedCount || 0);
            if (count > 0) {
                toast.success(`${count} salário(s) de holerite sincronizado(s) no caixa com sucesso!`);
            } else {
                toast.success(data?.message || 'Todos os holerites aprovados já estão sincronizados no caixa.');
            }
            queryClient.invalidateQueries(['cashbox-stats']);
            queryClient.invalidateQueries(['cashbox-transactions']);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Erro ao sincronizar holerites no caixa');
        }
    });

    // Memos
    const stats = useMemo(() => statsData?.data || null, [statsData]);
    const transactions = useMemo(() => transactionsData?.data?.transactions || [], [transactionsData]);
    const categories = useMemo(() => categoriesData?.data?.categories || [], [categoriesData]);

    const patrimonioBalance = useMemo(() => {
        return parseFloat(stats?.patrimonioBalance || 0);
    }, [stats]);

    const operationalBalance = useMemo(() => {
        const total = parseFloat(stats?.balance || 0);
        return total - patrimonioBalance;
    }, [stats, patrimonioBalance]);

    const categoriesByType = useMemo(() => {
        const grouped = { INCOME: [], EXPENSE: [], INVESTMENT: [], INVESTMENT_RETURN: [], TRANSFER_IN: [], TRANSFER_OUT: [] };
        categories.forEach(cat => {
            if (grouped[cat.type]) {
                grouped[cat.type].push(cat);
            }
        });
        if (grouped.INVESTMENT_RETURN.length === 0) grouped.INVESTMENT_RETURN = grouped.INCOME;

        return grouped;
    }, [categories]);

    const pagination = useMemo(() => transactionsData?.data?.pagination || { page: 1, pages: 1, total: 0 }, [transactionsData]);

    const handleFilterChange = () => {
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setFilterType('');
        setFilterCategory('');
        setFilterPaymentMethod('');
        setFilterMonth('');
        setFilterYear('');
        setFilterStartDate('');
        setFilterEndDate('');
        setCurrentPage(1);
    };

    const handleQuickRange = (preset) => {
        const today = new Date();
        setFilterMonth('');
        setFilterYear('');
        if (preset === 'today') {
            const d = format(today, 'yyyy-MM-dd');
            setFilterStartDate(d);
            setFilterEndDate(d);
        } else if (preset === 'yesterday') {
            const d = format(subDays(today, 1), 'yyyy-MM-dd');
            setFilterStartDate(d);
            setFilterEndDate(d);
        } else if (preset === '7days') {
            setFilterStartDate(format(subDays(today, 6), 'yyyy-MM-dd'));
            setFilterEndDate(format(today, 'yyyy-MM-dd'));
        } else if (preset === '30days') {
            setFilterStartDate(format(subDays(today, 29), 'yyyy-MM-dd'));
            setFilterEndDate(format(today, 'yyyy-MM-dd'));
        } else if (preset === 'thisMonth') {
            setFilterStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
            setFilterEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
        }
        setCurrentPage(1);
    };

    // Handlers
    const handleSubmit = (e) => {
        e.preventDefault();

        // Validar campos obrigatórios
        const amount = parseFloat(formData.amount);
        if (!amount || amount <= 0) {
            toast.error('Valor deve ser maior que zero');
            return;
        }
        if (!formData.category_code) {
            toast.error('Selecione uma categoria');
            return;
        }
        if (!formData.description || !formData.description.trim()) {
            toast.error('Descrição é obrigatória');
            return;
        }

        createMutation.mutate(formData);
    };

    const handleDelete = (id) => {
        setConfirmModal({ isOpen: true, transactionId: id });
    };

    const handleConfirmDelete = () => {
        if (confirmModal.transactionId) {
            deleteMutation.mutate(confirmModal.transactionId);
        }
        setConfirmModal({ isOpen: false, transactionId: null });
    };

    const handleCancelDelete = () => {
        setConfirmModal({ isOpen: false, transactionId: null });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({
            transaction_type: 'INCOME',
            category_code: '',
            description: '',
            amount: '',
            reference_date: format(new Date(), 'yyyy-MM-dd'),
            payment_method: 'TRANSFERENCIA',
            bank_account: '',
            document_number: '',
            notes: '',
            parent_id: null
        });
    };

    const handleOpenDetails = async (transaction) => {
        const isExpanding = transaction.id !== expandedTransaction;
        setExpandedTransaction(isExpanding ? transaction.id : null);

        if (isExpanding) {
            // Se já tem em cache, usa o cache
            const cached = queryClient.getQueryData(['cashbox-transaction-details', transaction.id]);
            if (cached) {
                setTransactionDetails(cached.data);
                return;
            }

            setIsDetailsLoading(true);
            try {
                const response = await cashboxService.getTransactionDetails(transaction.id);
                setTransactionDetails(response.data);
                // Armazena no cache
                queryClient.setQueryData(['cashbox-transaction-details', transaction.id], response.data);
            } catch (error) {
                toast.error('Erro ao buscar detalhes');
            } finally {
                setIsDetailsLoading(false);
            }
        }
    };

    const handleAddAporte = (parent) => {
        setFormData({
            ...formData,
            transaction_type: 'INVESTMENT',
            parent_id: parent.id,
            description: `Aporte em: ${parent.description}`,
            category_code: parent.category_code
        });
        setIsModalOpen(true);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const getTransactionTypeBadge = (type) => {
        const styles = {
            INCOME: 'bg-green-100 text-green-800 border-green-200',
            EXPENSE: 'bg-red-100 text-red-800 border-red-200',
            INVESTMENT: 'bg-emerald-100 text-emerald-800 border-emerald-200',
            INVESTMENT_RETURN: 'bg-green-100 text-green-800 border-green-200',
            TRANSFER_IN: 'bg-gray-100 text-gray-800 border-gray-200',
            TRANSFER_OUT: 'bg-gray-100 text-gray-800 border-gray-200'
        };

        const labels = {
            INCOME: 'Entrada',
            EXPENSE: 'Saída',
            INVESTMENT: 'Investimento (Entrada)',
            INVESTMENT_RETURN: 'Rendimento/Retorno',
            TRANSFER_IN: 'Transf. Entrada',
            TRANSFER_OUT: 'Transf. Saída'
        };

        return (
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${styles[type] || 'bg-gray-100'}`}>
                {labels[type] || type}
            </span>
        );
    };

    const getOriginBadge = (sourceType) => {
        const badges = {
            'MANUAL': { label: 'Entrada Manual', color: 'bg-blue-100 text-blue-700 border-blue-200' },
            'INITIAL_BALANCE': { label: 'Saldo Inicial', color: 'bg-amber-100 text-amber-700 border-amber-200' },
            'PARCEL_RECEIPT': { label: 'Parcela', color: 'bg-green-100 text-green-700 border-green-200' },
            'CASHBOX_INJECTION': { label: 'Injeção', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
            'HISTORICAL_MIGRATION': { label: 'Histórico', color: 'bg-purple-100 text-purple-700 border-purple-200' }
        };
        return badges[sourceType] || null;
    };

    const getBalancedBadge = (balanceType) => {
        if (!balanceType) return null;
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                🔗 Balanceado
            </span>
        );
    };

    const getPaymentMethodBadge = (method) => {
        if (!method || !method.trim()) {
            return (
                <span className="text-gray-400 font-normal text-xs select-none">
                    —
                </span>
            );
        }

        const normalized = method.trim().toUpperCase();

        if (normalized === 'PIX' || normalized.includes('PIX')) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-cyan-50 text-cyan-800 border border-cyan-200/80 shadow-xs">
                    <QrCode className="h-3 w-3 text-cyan-600 shrink-0" />
                    PIX
                </span>
            );
        }

        if (normalized === 'DINHEIRO' || normalized.includes('DINHEIR') || normalized.includes('ESPÉCIE') || normalized.includes('ESPECIE') || normalized === 'CASH') {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200/80 shadow-xs">
                    <Banknote className="h-3 w-3 text-emerald-600 shrink-0" />
                    Dinheiro
                </span>
            );
        }

        if (normalized === 'TRANSFERENCIA' || normalized === 'TRANSFERÊNCIA' || normalized.includes('TRANSF') || normalized === 'TED' || normalized === 'DOC') {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-800 border border-blue-200/80 shadow-xs">
                    <Building2 className="h-3 w-3 text-blue-600 shrink-0" />
                    Transferência
                </span>
            );
        }

        if (normalized === 'BALANCO_CAIXA' || normalized === 'BALANÇO_CAIXA' || normalized.includes('BALANCO') || normalized.includes('BALANÇO')) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-800 border border-indigo-200/80 shadow-xs">
                    <Scale className="h-3 w-3 text-indigo-600 shrink-0" />
                    Balanço Caixa
                </span>
            );
        }

        if (normalized === 'CARTAO' || normalized === 'CARTÃO' || normalized.includes('CART') || normalized === 'CARD') {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-800 border border-purple-200/80 shadow-xs">
                    <CreditCard className="h-3 w-3 text-purple-600 shrink-0" />
                    Cartão
                </span>
            );
        }

        if (normalized === 'BOLETO' || normalized.includes('BOLET')) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-800 border border-blue-200/80 shadow-xs">
                    <FileText className="h-3 w-3 text-blue-600 shrink-0" />
                    Boleto
                </span>
            );
        }

        if (normalized === 'CHEQUE' || normalized.includes('CHEQ') || normalized.includes('CHECK')) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200/80 shadow-xs">
                    <FileText className="h-3 w-3 text-amber-600 shrink-0" />
                    Cheque
                </span>
            );
        }

        if (normalized === 'FRACIONADO' || normalized.includes('FRACION')) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200/80 shadow-xs">
                    <CreditCard className="h-3 w-3 text-purple-600 shrink-0" />
                    Fracionado
                </span>
            );
        }

        if (normalized === 'MANUAL') {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200/80 shadow-xs">
                    Manual
                </span>
            );
        }

        if (normalized === 'OUTRO' || normalized.includes('OUTR')) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200/80 shadow-xs">
                    <Layers className="h-3 w-3 text-slate-500 shrink-0" />
                    Outro
                </span>
            );
        }

        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 shadow-xs">
                {method}
            </span>
        );
    };

    const TransactionRow = ({ t, isChild = false }) => {
        const isPayslip = t.source_type === 'PAYSLIP' || t.category_code === 'FOLHA_PAGAMENTO' || (t.description && (t.description.startsWith('Folha de Pagamento') || t.description.startsWith('Holerite') || t.description.startsWith('13º Salário') || t.description.startsWith('Rescisão')));

        return (
            <React.Fragment>
                <tr className={`hover:bg-slate-50/80 transition-colors duration-150 ${isChild ? 'bg-slate-50/40' : ''} ${t.balance_type ? 'bg-orange-50/40' : ''}`}>
                    <td className="p-4 text-gray-600 whitespace-nowrap text-sm">
                        {isChild && <span className="inline-block w-4 h-[1px] bg-gray-300 mr-2 mb-1" />}
                        {(() => {
                            const parts = t.reference_date ? t.reference_date.split('-') : [];
                            if (parts.length !== 3) return '-';
                            const dateVal = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                            return isNaN(dateVal.getTime()) ? '-' : format(dateVal, 'dd/MM/yyyy');
                        })()}
                    </td>
                    <td className="p-4">
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-gray-900 font-semibold text-sm">{t.description}</div>
                            {t.balance_type && getBalancedBadge(t.balance_type)}
                            {t.transaction_type === 'INVESTMENT' && !isChild && (
                                <button
                                    onClick={() => handleOpenDetails(t)}
                                    className="text-primary-600 hover:bg-primary-50 p-1 rounded-md transition-colors"
                                    title="Histórico do Investimento"
                                >
                                    <History className="h-4 w-4" />
                                </button>
                            )}
                            {isPayslip && (
                                <button
                                    onClick={() => handleOpenDetails(t)}
                                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors shadow-2xs"
                                    title="Ver Mini Holerite detalhado com proventos e descontos"
                                >
                                    <FileText className="h-3 w-3 text-blue-600" />
                                    <span>Mini Holerite</span>
                                </button>
                            )}
                        </div>
                        {t.bank_account && <div className="text-xs text-gray-500 font-medium mt-0.5">🏦 {t.bank_account}</div>}
                        {t.supplier_id && <div className="text-xs text-orange-600 font-medium mt-0.5">Fornecedor: {t.supplier_name || `#${t.supplier_id}`}</div>}
                        {t.project_id && <div className="text-xs text-blue-600 font-medium mt-0.5">Projeto #{t.project_id}</div>}
                        {t.client_name && <div className="text-xs text-gray-500 mt-0.5">Cliente: {t.client_name}</div>}
                        {t.project_description && <div className="text-xs text-gray-400 truncate max-w-xs mt-0.5">Projeto: {t.project_description}</div>}
                        {t.linked_transaction_id && <div className="text-xs text-gray-400 mt-0.5">🔗 Vinculada #{t.linked_transaction_id}</div>}
                    </td>
                    <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-gray-700 text-sm font-medium">{t.category_name}</span>
                          {t.source_type && getOriginBadge(t.source_type) && (
                            <span className={`inline-flex self-start px-2 py-0.5 rounded-full text-xs font-medium border ${getOriginBadge(t.source_type).color}`}>
                              {getOriginBadge(t.source_type).label}
                            </span>
                          )}
                        </div>
                    </td>
                    <td className="p-4 whitespace-nowrap">{getTransactionTypeBadge(t.transaction_type)}</td>
                    <td className="p-4 whitespace-nowrap">{getPaymentMethodBadge(t.payment_method)}</td>
                    <td className={`p-4 text-right font-semibold text-sm whitespace-nowrap tabular-nums ${['INCOME', 'INVESTMENT', 'INVESTMENT_RETURN', 'TRANSFER_IN'].includes(t.transaction_type) ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {['INCOME', 'INVESTMENT', 'INVESTMENT_RETURN', 'TRANSFER_IN'].includes(t.transaction_type) ? '+' : '-'}{formatCurrency(t.amount)}
                    </td>
                    <td className="p-4">
                        <div className="flex justify-center items-center gap-1.5">
                            {isPayslip && (
                                <button
                                    onClick={() => handleOpenDetails(t)}
                                    title="Ver Mini Holerite"
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                    <FileText className="h-4 w-4" />
                                </button>
                            )}
                            {t.transaction_type === 'INVESTMENT' && !isChild && (
                                <>
                                    <button
                                        onClick={() => handleAddAporte(t)}
                                        title="Novo Aporte"
                                        className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                    >
                                        <Plus className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => {
                                            setFormData({
                                                ...formData,
                                                transaction_type: 'INVESTMENT_RETURN',
                                                parent_id: t.id,
                                                description: `Rendimento: ${t.description}`,
                                                category_code: 'RETORNO_INVESTIMENTO'
                                            });
                                            setIsModalOpen(true);
                                        }}
                                        title="Registrar Rendimento"
                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    >
                                        <TrendingUp className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => {
                                            setFormData({
                                                ...formData,
                                                transaction_type: 'EXPENSE',
                                                parent_id: t.id,
                                                description: `Retirada de: ${t.description}`,
                                                category_code: 'RETIRADA_INVESTIMENTO'
                                            });
                                            setIsModalOpen(true);
                                        }}
                                        title="Registrar Retirada"
                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <ArrowDownCircle className="h-4 w-4" />
                                    </button>
                                </>
                            )}
                            <button
                                onClick={() => handleDelete(t.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
                                title="Excluir Transação"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </td>
                </tr>
                {expandedTransaction === t.id && (
                    <tr>
                        <td colSpan="7" className="p-0 bg-slate-50/70 border-b border-gray-200">
                            <div className="p-5 sm:p-6 border-l-4 border-primary-500 space-y-4">
                                {isDetailsLoading ? (
                                    <div className="flex items-center justify-center py-6 text-primary-600 gap-2">
                                        <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                                        <span className="text-xs font-semibold">Carregando detalhes...</span>
                                    </div>
                                ) : (isPayslip || transactionDetails?.payslip) ? (
                                    /* MINI HOLERITE COMPLETO E DETALHADO */
                                    <div className="bg-white rounded-2xl border border-blue-200 shadow-sm p-5 space-y-4 max-w-4xl">
                                        {/* Cabeçalho do Mini Holerite */}
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-gray-100 gap-2">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2.5 bg-blue-50 text-blue-700 rounded-xl border border-blue-100">
                                                    <FileText className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <h3 className="text-sm sm:text-base font-bold text-gray-900">
                                                            {transactionDetails?.payslip?.employee_name || t.description}
                                                        </h3>
                                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-800 border border-green-200">
                                                            Holerite Aprovado
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        Cargo: <strong className="text-gray-700">{transactionDetails?.payslip?.employee_role || 'Funcionário'}</strong> • Competência: <strong className="text-gray-700">{transactionDetails?.payslip ? `${String(transactionDetails.payslip.ref_month).padStart(2, '0')}/${transactionDetails.payslip.ref_year}` : '-'}</strong>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="sm:text-right">
                                                <span className="text-xs text-gray-400 font-mono">ID Holerite #{transactionDetails?.payslip?.id || t.source_id || '-'}</span>
                                                {transactionDetails?.payslip?.approved_at && (
                                                    <p className="text-[11px] text-gray-500">
                                                        Aprovado em: {format(new Date(transactionDetails.payslip.approved_at), 'dd/MM/yyyy HH:mm')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* 2 Colunas: Proventos / Vencimentos (+) e Descontos (-) */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Coluna 1: Proventos (+) */}
                                            <div className="bg-emerald-50/40 border border-emerald-200/80 rounded-xl p-3.5 space-y-2">
                                                <div className="flex items-center justify-between pb-1.5 border-b border-emerald-200/60 text-emerald-900">
                                                    <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                                                        <TrendingUp className="w-3.5 h-3.5 text-emerald-700" />
                                                        <span>Proventos / Vencimentos (+)</span>
                                                    </span>
                                                </div>
                                                <div className="space-y-1.5 text-xs">
                                                    <div className="flex justify-between text-gray-700">
                                                        <span>Salário Mensal / Base:</span>
                                                        <span className="font-semibold">{formatCurrency(transactionDetails?.payslip?.monthly_base_salary || transactionDetails?.payslip?.regular_salary_weekday || t.amount)}</span>
                                                    </div>
                                                    {(Number(transactionDetails?.payslip?.overtime_salary_weekday || 0) > 0 || Number(transactionDetails?.payslip?.overtime_salary_weekend || 0) > 0) && (
                                                        <div className="flex justify-between text-gray-700">
                                                            <span>Horas Extras:</span>
                                                            <span className="font-semibold text-emerald-700">+{formatCurrency(Number(transactionDetails?.payslip?.overtime_salary_weekday || 0) + Number(transactionDetails?.payslip?.overtime_salary_weekend || 0))}</span>
                                                        </div>
                                                    )}
                                                    {Number(transactionDetails?.payslip?.fixed_saturday_amount || 0) > 0 && (
                                                        <div className="flex justify-between text-gray-700">
                                                            <span>Sábados Trabalhados:</span>
                                                            <span className="font-semibold text-emerald-700">+{formatCurrency(transactionDetails?.payslip?.fixed_saturday_amount)}</span>
                                                        </div>
                                                    )}
                                                    {Number(transactionDetails?.payslip?.holiday_bonus_amount || 0) > 0 && (
                                                        <div className="flex justify-between text-gray-700">
                                                            <span>Bônus / Feriado:</span>
                                                            <span className="font-semibold text-emerald-700">+{formatCurrency(transactionDetails?.payslip?.holiday_bonus_amount)}</span>
                                                        </div>
                                                    )}
                                                    {Number(transactionDetails?.payslip?.extra_monthly_amount || 0) > 0 && (
                                                        <div className="flex justify-between text-gray-700">
                                                            <span>Abono Extra Mensal:</span>
                                                            <span className="font-semibold text-emerald-700">+{formatCurrency(transactionDetails?.payslip?.extra_monthly_amount)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Coluna 2: Descontos (-) */}
                                            <div className="bg-rose-50/40 border border-rose-200/80 rounded-xl p-3.5 space-y-2">
                                                <div className="flex items-center justify-between pb-1.5 border-b border-rose-200/60 text-rose-900">
                                                    <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                                                        <TrendingDown className="w-3.5 h-3.5 text-rose-700" />
                                                        <span>Descontos (-)</span>
                                                    </span>
                                                </div>
                                                <div className="space-y-1.5 text-xs">
                                                    {Number(transactionDetails?.payslip?.inss_deduction || 0) > 0 && (
                                                        <div className="flex justify-between text-gray-700">
                                                            <span>INSS ({transactionDetails?.payslip?.inss_percentage || 7.5}%):</span>
                                                            <span className="font-semibold text-rose-700">-{formatCurrency(transactionDetails?.payslip?.inss_deduction)}</span>
                                                        </div>
                                                    )}
                                                    {Number(transactionDetails?.payslip?.advance_deduction || 0) > 0 && (
                                                        <div className="flex justify-between text-gray-700">
                                                            <span>Adiantamento Quinzenal:</span>
                                                            <span className="font-semibold text-rose-700">-{formatCurrency(transactionDetails?.payslip?.advance_deduction)}</span>
                                                        </div>
                                                    )}
                                                    {Number(transactionDetails?.payslip?.diverse_expenses_total || 0) > 0 && (
                                                        <div className="flex justify-between text-gray-700">
                                                            <span>Vales / Empréstimos:</span>
                                                            <span className="font-semibold text-rose-700">-{formatCurrency(transactionDetails?.payslip?.diverse_expenses_total)}</span>
                                                        </div>
                                                    )}
                                                    {Number(transactionDetails?.payslip?.absence_base_deduction || 0) > 0 && (
                                                        <div className="flex justify-between text-gray-700">
                                                            <span>Ausências / Faltas:</span>
                                                            <span className="font-semibold text-rose-700">-{formatCurrency(transactionDetails?.payslip?.absence_base_deduction)}</span>
                                                        </div>
                                                    )}
                                                    {Number(transactionDetails?.payslip?.inss_deduction || 0) === 0 && Number(transactionDetails?.payslip?.advance_deduction || 0) === 0 && Number(transactionDetails?.payslip?.diverse_expenses_total || 0) === 0 && (
                                                        <p className="text-gray-400 italic">Nenhum desconto aplicado nesta competência.</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Destaque Salário Líquido Pago */}
                                        <div className="bg-emerald-600 text-white rounded-xl p-3.5 flex items-center justify-between shadow-xs">
                                            <div>
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-100">
                                                    Salário Líquido Pago (Débito Efetivo do Caixa)
                                                </span>
                                                <p className="text-xl sm:text-2xl font-extrabold font-mono mt-0.5">
                                                    {formatCurrency(transactionDetails?.payslip?.total_salary || t.amount)}
                                                </p>
                                            </div>
                                            {transactionDetails?.payslip?.has_work_card && (
                                                <div className="text-right">
                                                    <span className="text-[10px] font-semibold text-emerald-200">FGTS Empresa (8%):</span>
                                                    <p className="text-xs font-bold text-white font-mono">
                                                        {formatCurrency(transactionDetails?.payslip?.fgts_deduction || (Number(transactionDetails?.payslip?.inss_base || transactionDetails?.payslip?.monthly_base_salary || 0) * 0.08))}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Detalhes de Pagamento do Holerite (Forma de Pagamento, Fracionamento e Comprovante) */}
                                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2 text-xs">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-gray-700 uppercase tracking-wider text-[11px]">Pagamento Realizado:</span>
                                                    {getPaymentMethodBadge(t.payment_method)}
                                                    {transactionDetails?.payslip?.paid_at && (
                                                        <span className="text-gray-500 font-medium">
                                                            em {format(new Date(transactionDetails.payslip.paid_at), 'dd/MM/yyyy')}
                                                        </span>
                                                    )}
                                                </div>
                                                {transactionDetails?.payslip?.receipt_file_url && (
                                                    <a
                                                        href={transactionDetails.payslip.receipt_file_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 rounded-lg font-bold transition-colors"
                                                    >
                                                        <Paperclip className="h-3.5 w-3.5 text-emerald-600" />
                                                        <span>Ver Comprovante Anexo</span>
                                                    </a>
                                                )}
                                            </div>

                                            {Array.isArray(transactionDetails?.payslip?.payment_splits) && transactionDetails.payslip.payment_splits.length > 0 && (
                                                <div className="pt-2 border-t border-slate-200">
                                                    <span className="text-[11px] font-bold text-purple-900 block mb-1">
                                                        Detalhamento do Pagamento Fracionado:
                                                    </span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {transactionDetails.payslip.payment_splits.map((split, sIdx) => (
                                                            <div key={sIdx} className="px-2.5 py-1 bg-white border border-purple-200 rounded-lg text-purple-900 font-semibold flex items-center gap-1.5 shadow-2xs">
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-100 uppercase">{split.method}</span>
                                                                <span>{formatCurrency(split.amount)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {transactionDetails?.payslip?.payment_notes && (
                                                <div className="text-gray-600 pt-1 border-t border-slate-200">
                                                    <strong className="text-gray-700">Observações:</strong> {transactionDetails.payslip.payment_notes}
                                                </div>
                                            )}
                                        </div>

                                        {/* Detalhes de Vales ou Aprovações */}
                                        {transactionDetails?.payslip?.approvals?.length > 0 && (
                                            <div className="pt-2 border-t border-gray-100 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                <span className="font-semibold text-gray-700">Aprovado por:</span>
                                                {transactionDetails.payslip.approvals.map((ap, i) => (
                                                    <span key={i} className="px-2 py-0.5 bg-gray-100 rounded-md font-medium text-gray-800">
                                                        ✓ {ap.approver_name} ({format(new Date(ap.approved_at), 'dd/MM/yyyy HH:mm')})
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                                <p className="text-xs text-gray-500 uppercase font-bold">Investimento Inicial</p>
                                                <p className="text-lg font-bold text-gray-900">{formatCurrency(t.amount)}</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                                <p className="text-xs text-gray-500 uppercase font-bold">Aportes/Rendimentos</p>
                                                <p className="text-lg font-bold text-green-600">
                                                    +{formatCurrency(transactionDetails?.children?.reduce((acc, c) => acc + (['INCOME', 'INVESTMENT', 'INVESTMENT_RETURN', 'TRANSFER_IN'].includes(c.transaction_type) ? parseFloat(c.amount) : 0), 0))}
                                                </p>
                                            </div>
                                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                                <p className="text-xs text-gray-500 uppercase font-bold">Retiradas Acumuladas</p>
                                                <p className="text-lg font-bold text-red-600">
                                                    -{formatCurrency(transactionDetails?.children?.reduce((acc, c) => acc + (['EXPENSE', 'TRANSFER_OUT'].includes(c.transaction_type) ? parseFloat(c.amount) : 0), 0))}
                                                </p>
                                            </div>
                                            <div className="bg-white p-3 rounded-lg border border-primary-100 shadow-sm">
                                                <p className="text-xs text-gray-500 uppercase font-bold text-primary-700">Saldo Atualizado</p>
                                                <p className="text-lg font-bold text-primary-700">
                                                    {formatCurrency(parseFloat(t.amount) +
                                                        (transactionDetails?.children?.reduce((acc, c) => acc + (['INCOME', 'INVESTMENT', 'INVESTMENT_RETURN', 'TRANSFER_IN'].includes(c.transaction_type) ? parseFloat(c.amount) : -parseFloat(c.amount)), 0) || 0)
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        {transactionDetails?.children?.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                                    <TrendingUp className="h-4 w-4" /> Detalhamento de Movimentações
                                                </h4>
                                                <table className="w-full text-xs bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                                    <thead className="bg-gray-50 text-gray-500 uppercase font-bold">
                                                        <tr>
                                                            <th className="p-2 text-left">Data</th>
                                                            <th className="p-2 text-left">Descrição</th>
                                                            <th className="p-2 text-right">Valor</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {transactionDetails.children.map(child => (
                                                            <tr key={child.id} className="hover:bg-gray-50 transition-colors">
                                                                <td className="p-2 text-gray-500">{(() => { const parts = child.reference_date.split('-'); if (parts.length !== 3) return '-'; const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])); return isNaN(d.getTime()) ? '-' : format(d, 'dd/MM/yyyy'); })()}</td>
                                                                <td className="p-2 font-medium">
                                                                    {child.description}
                                                                    <span className="ml-2 px-1.5 py-0.5 bg-gray-100 text-[10px] rounded text-gray-400 uppercase">
                                                                        {child.category_name}
                                                                    </span>
                                                                </td>
                                                                <td className={`p-2 text-right font-bold ${['INCOME', 'INVESTMENT', 'INVESTMENT_RETURN', 'TRANSFER_IN'].includes(child.transaction_type) ? 'text-green-600' : 'text-red-600'}`}>
                                                                    {['INCOME', 'INVESTMENT', 'INVESTMENT_RETURN', 'TRANSFER_IN'].includes(child.transaction_type) ? '+' : '-'}{formatCurrency(child.amount)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {transactionDetails?.history?.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                                    <AlertCircle className="h-4 w-4" /> Histórico de Edições
                                                </h4>
                                                <div className="space-y-2">
                                                    {transactionDetails.history.map(ed => (
                                                        <div key={ed.id} className="text-xs bg-white p-2 rounded-lg border border-gray-200 text-gray-600">
                                                            <span className="font-bold">{format(new Date(ed.edited_at), 'dd/MM/yyyy HH:mm')}</span> - Editado por <span className="text-gray-900">{ed.edited_by_name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    if (isLoadingStats || isLoadingTransactions) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-100 shadow-xs">
                <div className="flex items-center gap-3.5">
                    <div className="p-3 bg-primary-50 rounded-2xl text-primary-600 border border-primary-100/60 shadow-xs">
                        <Wallet className="h-6 w-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Caixa da Empresa</h1>
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-100">
                                Gestão Financeira
                            </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">Gestão de capital inicial, investimentos e movimentações extraordinárias</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2.5">
                    <button
                        onClick={() => syncPayslipsMutation.mutate()}
                        disabled={syncPayslipsMutation.isPending}
                        className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-blue-50 text-blue-700 border border-blue-200/80 rounded-xl hover:bg-blue-100 font-semibold text-sm transition-all shadow-xs disabled:opacity-50"
                        title="Lança no Caixa da Empresa todos os holerites aprovados que ainda não constem no livro caixa"
                    >
                        <FileText className="h-4 w-4" />
                        {syncPayslipsMutation.isPending ? 'Sincronizando...' : 'Sincronizar Folha'}
                    </button>
                    <button
                        onClick={() => setShowInjection(true)}
                        className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-emerald-50 text-emerald-700 border border-emerald-200/80 rounded-xl hover:bg-emerald-100 font-semibold text-sm transition-all shadow-xs"
                    >
                        <Upload className="h-4 w-4" />
                        Injeção de Saldo
                    </button>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-sm hover:shadow font-semibold text-sm"
                    >
                        <Plus className="h-4 w-4" />
                        Nova Transação
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Saldo Operacional */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-600" />
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Saldo Operacional</span>
                            <button
                                onClick={() => setShowRecalculateConfirm(true)}
                                disabled={recalculateMutation.isPending}
                                className="p-1 hover:bg-blue-50 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"
                                title="Recalcular saldo"
                            >
                                <RefreshCw className={`h-3.5 w-3.5 ${recalculateMutation.isPending ? 'animate-spin text-blue-600' : ''}`} />
                            </button>
                        </div>
                        <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl group-hover:scale-105 transition-transform">
                            <Wallet className="h-5 w-5" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 tracking-tight">{formatCurrency(operationalBalance)}</p>
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                        Exclui patrimônio
                    </div>
                </div>

                {/* Saldo Patrimônio */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 to-teal-600" />
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Saldo Patrimônio</span>
                        <div className="p-2.5 bg-teal-50 text-teal-600 rounded-xl group-hover:scale-105 transition-transform">
                            <TrendingUp className="h-5 w-5" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600 tracking-tight">{formatCurrency(patrimonioBalance)}</p>
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                        Doc. Fiscais
                    </div>
                </div>

                {/* Entradas */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-green-500 to-emerald-600" />
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Entradas</span>
                        <div className="p-2.5 bg-green-50 text-green-600 rounded-xl group-hover:scale-105 transition-transform">
                            <ArrowUpRight className="h-5 w-5" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-green-600 tracking-tight">{formatCurrency(stats?.totals?.incomes)}</p>
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-green-600/70 font-medium">
                        Receitas acumuladas
                    </div>
                </div>

                {/* Saídas/Despesas */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-rose-500 to-red-600" />
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Saídas</span>
                        <div className="p-2.5 bg-rose-50 text-rose-600 rounded-xl group-hover:scale-105 transition-transform">
                            <ArrowDownRight className="h-5 w-5" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-rose-600 tracking-tight">{formatCurrency(stats?.totals?.expenses)}</p>
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-rose-600/70 font-medium">
                        Despesas acumuladas
                    </div>
                </div>
            </div>

            {/* Filtros e Tabela */}
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
                <div className="p-5 bg-slate-50/60 border-b border-gray-200/80 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {/* Search */}
                        <div className="relative sm:col-span-2 lg:col-span-2">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Pesquisar descrição, notas ou cliente..."
                                className="input pl-9 w-full bg-white border-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-sm rounded-xl"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Tipo */}
                        <div>
                            <select
                                className="input w-full bg-white border-gray-200 text-sm rounded-xl font-medium text-gray-700"
                                value={filterType}
                                onChange={(e) => { setFilterType(e.target.value); handleFilterChange(); }}
                            >
                                <option value="">Todos os Tipos</option>
                                <option value="INCOME">Entradas</option>
                                <option value="EXPENSE">Saídas</option>
                                <option value="INVESTMENT">Investimentos</option>
                                <option value="INVESTMENT_RETURN">Retorno Inv.</option>
                            </select>
                        </div>

                        {/* Categoria */}
                        <div>
                            <select
                                className="input w-full bg-white border-gray-200 text-sm rounded-xl font-medium text-gray-700"
                                value={filterCategory}
                                onChange={(e) => { setFilterCategory(e.target.value); handleFilterChange(); }}
                            >
                                <option value="">Todas as Categorias</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.code}>{cat.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Forma de Pagamento */}
                        <div>
                            <select
                                className="input w-full bg-white border-gray-200 text-sm rounded-xl font-medium text-gray-700"
                                value={filterPaymentMethod}
                                onChange={(e) => { setFilterPaymentMethod(e.target.value); handleFilterChange(); }}
                            >
                                <option value="">Todos os Métodos</option>
                                <option value="PIX">PIX</option>
                                <option value="DINHEIRO">Dinheiro em Espécie</option>
                                <option value="TRANSFERENCIA">Transferência Bancária</option>
                                <option value="FRACIONADO">Fracionado (Holerite / Múltiplo)</option>
                                <option value="BALANCO_CAIXA">Balanço de Caixa</option>
                                <option value="CARTAO">Cartão</option>
                                <option value="CHEQUE">Cheque</option>
                                <option value="OUTRO">Outro</option>
                            </select>
                        </div>

                        {/* Mês */}
                        <div>
                            <select
                                className="input w-full bg-white border-gray-200 text-sm rounded-xl font-medium text-gray-700"
                                value={filterMonth}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFilterMonth(val);
                                    if (val) {
                                        setFilterStartDate('');
                                        setFilterEndDate('');
                                    }
                                    handleFilterChange();
                                }}
                            >
                                <option value="">Todos os Meses</option>
                                <option value="01">Janeiro</option>
                                <option value="02">Fevereiro</option>
                                <option value="03">Março</option>
                                <option value="04">Abril</option>
                                <option value="05">Maio</option>
                                <option value="06">Junho</option>
                                <option value="07">Julho</option>
                                <option value="08">Agosto</option>
                                <option value="09">Setembro</option>
                                <option value="10">Outubro</option>
                                <option value="11">Novembro</option>
                                <option value="12">Dezembro</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end pt-1">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Ano</label>
                            <select
                                className="input w-full bg-white border-gray-200 text-sm rounded-xl font-medium text-gray-700"
                                value={filterYear}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFilterYear(val);
                                    if (val) {
                                        setFilterStartDate('');
                                        setFilterEndDate('');
                                    }
                                    handleFilterChange();
                                }}
                            >
                                <option value="">Todos os Anos</option>
                                <option value="2024">2024</option>
                                <option value="2025">2025</option>
                                <option value="2026">2026</option>
                                <option value="2027">2027</option>
                                <option value="2028">2028</option>
                            </select>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-semibold text-gray-500">Data Início</label>
                                {filterStartDate && (
                                    <button
                                        type="button"
                                        onClick={() => { setFilterStartDate(''); handleFilterChange(); }}
                                        className="text-[10px] text-gray-400 hover:text-red-500 flex items-center gap-0.5"
                                        title="Limpar data de início"
                                    >
                                        <X className="w-3 h-3" /> Limpar
                                    </button>
                                )}
                            </div>
                            <input
                                type="date"
                                min="2000-01-01"
                                max="2099-12-31"
                                className="input w-full bg-white border-gray-200 text-sm rounded-xl"
                                value={filterStartDate}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFilterStartDate(val);
                                    if (val) {
                                        setFilterMonth('');
                                        setFilterYear('');
                                    }
                                    handleFilterChange();
                                }}
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-semibold text-gray-500">Data Fim</label>
                                {filterEndDate && (
                                    <button
                                        type="button"
                                        onClick={() => { setFilterEndDate(''); handleFilterChange(); }}
                                        className="text-[10px] text-gray-400 hover:text-red-500 flex items-center gap-0.5"
                                        title="Limpar data de fim"
                                    >
                                        <X className="w-3 h-3" /> Limpar
                                    </button>
                                )}
                            </div>
                            <input
                                type="date"
                                min={validStartDate || "2000-01-01"}
                                max="2099-12-31"
                                className={`input w-full bg-white text-sm rounded-xl ${
                                    isDateRangeInvalid
                                        ? 'border-red-400 text-red-700 focus:border-red-500 focus:ring-1 focus:ring-red-400'
                                        : 'border-gray-200'
                                }`}
                                value={filterEndDate}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFilterEndDate(val);
                                    if (val) {
                                        setFilterMonth('');
                                        setFilterYear('');
                                    }
                                    handleFilterChange();
                                }}
                            />
                        </div>
                        <div>
                            <button
                                onClick={handleClearFilters}
                                className="btn-secondary w-full h-10 rounded-xl flex items-center justify-center gap-2 text-gray-700 hover:text-gray-900 border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium shadow-2xs"
                            >
                                <RotateCcw className="h-4 w-4 text-gray-500" />
                                Limpar Filtros
                            </button>
                        </div>
                    </div>

                    {/* Atalhos Rápidos e Alerta de Range Inválido */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-200/60">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mr-0.5">Atalhos:</span>
                            <button
                                type="button"
                                onClick={() => handleQuickRange('today')}
                                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors shadow-2xs"
                            >
                                Hoje
                            </button>
                            <button
                                type="button"
                                onClick={() => handleQuickRange('7days')}
                                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors shadow-2xs"
                            >
                                Últimos 7 dias
                            </button>
                            <button
                                type="button"
                                onClick={() => handleQuickRange('30days')}
                                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors shadow-2xs"
                            >
                                Últimos 30 dias
                            </button>
                            <button
                                type="button"
                                onClick={() => handleQuickRange('thisMonth')}
                                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors shadow-2xs"
                            >
                                Este Mês
                            </button>
                        </div>

                        {isDateRangeInvalid && (
                            <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-lg">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                <span>A <strong>Data Fim</strong> não pode ser anterior à <strong>Data Início</strong>.</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/90 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                <th className="p-4">Data</th>
                                <th className="p-4">Descrição</th>
                                <th className="p-4">Categoria</th>
                                <th className="p-4">Tipo</th>
                                <th className="p-4">Pagamento</th>
                                <th className="p-4 text-right">Valor</th>
                                <th className="p-4 text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 font-medium">
                            {transactions.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="p-12 text-center text-gray-500 italic">
                                        Nenhuma transação encontrada com os filtros selecionados.
                                    </td>
                                </tr>
                            ) : (
                                transactions.map((t) => (
                                    <TransactionRow key={t.id} t={t} />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {pagination.pages > 1 && (
                    <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                            Mostrando <span className="font-semibold">{(pagination.page - 1) * pageSize + 1}</span> a{' '}
                            <span className="font-semibold">{Math.min(pagination.page * pageSize, pagination.total)}</span> de{' '}
                            <span className="font-semibold">{pagination.total}</span> registros
                        </div>
                        <div className="flex items-center gap-2">
                            <select
                                className="input h-8 text-sm"
                                value={pageSize}
                                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                            >
                                <option value={10}>10 por página</option>
                                <option value={20}>20 por página</option>
                                <option value={50}>50 por página</option>
                                <option value={100}>100 por página</option>
                            </select>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={pagination.page === 1}
                                className="btn-secondary h-8 px-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Anterior
                            </button>
                            <span className="text-sm text-gray-600">
                                Página <span className="font-semibold">{pagination.page}</span> de{' '}
                                <span className="font-semibold">{pagination.pages}</span>
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                                disabled={pagination.page === pagination.pages}
                                className="btn-secondary h-8 px-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-40 backdrop-blur-sm">
                    <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-900">Nova Movimentação de Caixa</h2>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de Operação</label>
                                    <select
                                        className="input"
                                        value={formData.transaction_type}
                                        onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value, category_code: '' })}
                                    >
                                        <option value="INCOME">Entrada de Valor</option>
                                        <option value="EXPENSE">Saída de Valor</option>
                                        <option value="INVESTMENT">Investimento</option>
                                        <option value="INVESTMENT_RETURN">Retorno de Investimento</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Categoria</label>
                                    <select
                                        className="input"
                                        required
                                        value={formData.category_code}
                                        onChange={(e) => setFormData({ ...formData, category_code: e.target.value })}
                                    >
                                        <option value="">Selecione...</option>
                                        {categoriesByType[formData.transaction_type]?.map(cat => (
                                            <option key={cat.id} value={cat.code}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {formData.parent_id && (
                                    <div className="md:col-span-2">
                                        <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                                                <TrendingUp className="h-4 w-4" /> Vinculado ao investimento pai (ID: {formData.parent_id})
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, parent_id: null })}
                                                className="text-blue-500 hover:text-blue-700"
                                            >
                                                Remover vínculo
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Descrição</label>
                                    <input
                                        type="text"
                                        required
                                        className="input"
                                        placeholder="Ex: Aporte inicial do sócio, Compra de Servidor"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Valor (R$)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        required
                                        className="input font-bold"
                                        placeholder="0,00"
                                        value={formData.amount}
                                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Data</label>
                                    <input
                                        type="date"
                                        required
                                        className="input"
                                        value={formData.reference_date}
                                        onChange={(e) => setFormData({ ...formData, reference_date: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Forma de Pagamento</label>
                                    <select
                                        className="input"
                                        value={formData.payment_method}
                                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                                    >
                                        <option value="TRANSFERENCIA">Transferência Bancária</option>
                                        <option value="PIX">PIX</option>
                                        <option value="DINHEIRO">Dinheiro em Espécie</option>
                                        <option value="BALANCO_CAIXA">Balanço de Caixa</option>
                                        <option value="CARTAO">Cartão</option>
                                        <option value="CHEQUE">Cheque</option>
                                        <option value="OUTRO">Outro</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Conta/Banco</label>
                                    <input
                                        type="text"
                                        className="input"
                                        placeholder="Ex: Santander, Caixa, Cofre"
                                        value={formData.bank_account}
                                        onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Nº Documento/Ref</label>
                                    <input
                                        type="text"
                                        className="input"
                                        placeholder="Opcional"
                                        value={formData.document_number}
                                        onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Observações Adicionais</label>
                                <textarea
                                    className="input min-h-[80px]"
                                    rows="2"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                ></textarea>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="btn-secondary flex-1"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={createMutation.isLoading}
                                    className="btn-primary flex-1 shadow-lg shadow-primary-100"
                                >
                                    {createMutation.isLoading ? 'Registrando...' : 'Confirmar Lançamento'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Injeção de Saldo */}
            {showInjection && (
                <CashboxInjection
                    onClose={() => setShowInjection(false)}
                    onSuccess={() => {
                        queryClient.invalidateQueries(['cashbox-stats']);
                        queryClient.invalidateQueries(['cashbox-transactions']);
                    }}
                />
            )}

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title="Excluir Transação"
                message="Tem certeza que deseja excluir esta transação? O saldo será revertido."
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />

            <ConfirmModal
                isOpen={showRecalculateConfirm}
                title="Auditar e Recalcular Saldo do Caixa"
                message="Deseja recalcular o caixa? Esta ação irá auditar todas as movimentações em ordem cronológica e sincronizar o saldo real no centavo."
                onConfirm={() => {
                    setShowRecalculateConfirm(false);
                    recalculateMutation.mutate();
                }}
                onCancel={() => setShowRecalculateConfirm(false)}
            />
        </div>
    );
};

export default CompanyCashbox;
